import * as React from 'react';
import Modal from 'react-modal';
import { InjectedRouter, PlainRoute } from 'react-router';
import { Location } from 'lib/ReactRouterTypes';
import AdminOrgApprovalSettingsConstants from '../constants/AdminOrgApprovalSettingsConstants';
import Button from 'components/ButtonDeprecated';
import ConfirmDialog from 'components/ConfirmDialog';
import Container from 'components/Container';
import CurrentUser from 'auth/CurrentUser';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import Radio from 'components/Radio';
import {
  renderRadioSelectLabel,
  renderRadioSelectField,
} from 'admin/components/AdminDefaultClassSettings/components/AdminDefaultClassSettingsView';
import {
  OrganizationType,
  SetOrgApprovalSettingsInput,
  SetDistrictSettingsInput,
} from 'globalTypes';
import Text from 'components/Text';
import TitleHeader from 'components/TitleHeader';
import Tooltip from 'components/Tooltip';
import Group from 'entities/Group';
import Divider from 'components/Divider';
import GroupActions from 'groups/actions/GroupActions';
import ToastActions from 'actions/ToastActions';
import { debounce } from 'lodash';
import { GROUP_CODE_VALIDATION_REGEX } from 'groups/components/GroupForm';
import { assertions } from 'lib/assertions';
import FormElement from 'components/FormElement';
import PrependedTextInput from 'components/PrependedTextInput';
import errorLogger from 'utils/errorLogger';
import SettingsStore from 'stores/SettingsStore';
import { loadStaffClassForOrgId, loadGQLUserGroupByCode } from 'groups/lib/GroupPromises.ts';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Checkbox from 'components/Checkbox';

enum OrgTeacherSignupState {
  open = 'open',
  requireApproval = 'requireApproval',
}

enum OrgClassCreationState {
  open = 'open',
  requireApproval = 'requireApproval',
}

enum SchoolAdminsCanSendUrgentMessageState {
  on = 'on',
  off = 'off',
}

export type Props = {
  orgId: number;
  orgUuid: string;
  orgType: OrganizationType;
  groupCode: string;
  route: PlainRoute;
  router: InjectedRouter;
  location: Location;
  approvalSettings: SetOrgApprovalSettingsInput;
  onOrgApprovalSettingsSave: (arg0: { variables: { input: SetOrgApprovalSettingsInput } }) => void;
  districtSettings: any;
  onDistrictSettingsSave: (arg0: { variables: { input: SetDistrictSettingsInput } }) => void;
  canSendUrgentMessage: boolean;
};

type State = {
  approvePendingRequestsDialogIsOpen: boolean;
  backfillDialogIsOpen: boolean;
  unsavedChangesDialogIsOpen: boolean;
  studentAndParentClassCreationInitialSelect?: OrgClassCreationState;
  teacherSignupInitialSelect?: OrgTeacherSignupState;
  studentAndParentClassCreationSelect?: OrgClassCreationState;
  teacherSignupSelect?: OrgTeacherSignupState;
  schoolAdminsCanSendUrgentMessageSelect?: SchoolAdminsCanSendUrgentMessageState;
  schoolAdminsCanSendUrgentMessageInitialSelect?: SchoolAdminsCanSendUrgentMessageState;
  classCodeInitialText: string;
  classCodeText: string;
  classCodeValid: boolean;
  nextLocation?: Location;
  classCodeError: string | null;
  verifyingCodeUniqueness: boolean;
  confirmTeacherSignupRequireApprovalDialogIsOpen: boolean;
  confirmAllRequireApprovalDialogIsOpen: boolean;
};

export default class AdminOrgApprovalSettingsView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      approvePendingRequestsDialogIsOpen: false,
      backfillDialogIsOpen: false,
      unsavedChangesDialogIsOpen: false,
      classCodeValid: false,
      classCodeText: props.groupCode,
      classCodeInitialText: props.groupCode,
      classCodeError: null,
      verifyingCodeUniqueness: false,
      confirmTeacherSignupRequireApprovalDialogIsOpen: false,
      confirmAllRequireApprovalDialogIsOpen: false,
    };
  }

  _isMounted: boolean = false;

  isMounted() {
    return this._isMounted;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidMount() {
    // https://github.com/ReactTraining/react-router/blob/v3/docs/API.md#setrouteleavehookroute-hook
    // this registers `handleAttemptedRouteChange` to fire anytime the user tries to leave the current route
    this.props.router?.setRouteLeaveHook(this.props.route, this.handleAttemptedRouteChange);

    const { approvalSettings, districtSettings } = this.props;
    const { gateTeacherRole, gateClasses } = approvalSettings;
    const teacherSignupValue = gateTeacherRole
      ? OrgTeacherSignupState.requireApproval
      : OrgTeacherSignupState.open;
    const classCreationValue = gateClasses
      ? OrgClassCreationState.requireApproval
      : OrgClassCreationState.open;
    const schoolAdminsCanSendUrgentMessageValue = districtSettings.schoolAdminsCanSendUrgentMessage
      ? SchoolAdminsCanSendUrgentMessageState.on
      : SchoolAdminsCanSendUrgentMessageState.off;

    this.setState({
      teacherSignupInitialSelect: teacherSignupValue,
      teacherSignupSelect: teacherSignupValue,
      studentAndParentClassCreationInitialSelect: classCreationValue,
      studentAndParentClassCreationSelect: classCreationValue,
      classCodeValid: true,
      schoolAdminsCanSendUrgentMessageInitialSelect: schoolAdminsCanSendUrgentMessageValue,
      schoolAdminsCanSendUrgentMessageSelect: schoolAdminsCanSendUrgentMessageValue,
    });
    this._isMounted = true;
  }

  showUnsavedChangesDialog = () => {
    this.setState({ unsavedChangesDialogIsOpen: true });
  };

  hideUnsavedChangesDialog = () => {
    this.setState({ unsavedChangesDialogIsOpen: false });
  };

  handleAttemptedRouteChange = (nextLocation?: Location) => {
    // if the user has changed their settings and isn't already viewing the unsaved changes dialog,
    // we should mount the dialog and store the nextLocation that the user attempted to visit
    if (this.userHasUnsavedChanges() && nextLocation && !this.state.unsavedChangesDialogIsOpen) {
      this.setState({ nextLocation }, this.showUnsavedChangesDialog);
      return false;
    }
  };

  forwardUserToNextLocation = () => {
    // we should only be calling this method from the confirmation dialog, so we *should* always
    // have a nextLocation, but we don't initialize this component with a nextLocation so we
    // need to perform this check
    const { nextLocation } = this.state;
    return nextLocation
      ? this.props.router.push(nextLocation)
      : console.error(
          'AdminDefaultClassSettingsView - tried to route user to nonexistent nextLocation'
        );
  };

  userHasChangedApprovalSettings = () => {
    const {
      teacherSignupSelect,
      teacherSignupInitialSelect,
      studentAndParentClassCreationSelect,
      studentAndParentClassCreationInitialSelect,
    } = this.state;

    return (
      teacherSignupSelect !== teacherSignupInitialSelect ||
      studentAndParentClassCreationSelect !== studentAndParentClassCreationInitialSelect
    );
  };

  userHasChangedClassCode = () => {
    const { classCodeText, classCodeInitialText } = this.state;

    return classCodeText !== classCodeInitialText;
  };

  userHasChangedSchoolAdminsCanSendUrgentMessage = () => {
    const {
      schoolAdminsCanSendUrgentMessageSelect,
      schoolAdminsCanSendUrgentMessageInitialSelect,
    } = this.state;

    return schoolAdminsCanSendUrgentMessageSelect !== schoolAdminsCanSendUrgentMessageInitialSelect;
  };

  userHasUnsavedChanges = () => {
    return (
      this.userHasChangedClassCode() ||
      this.userHasChangedApprovalSettings() ||
      this.userHasChangedSchoolAdminsCanSendUrgentMessage()
    );
  };

  renderBackfillModalParagraph = (
    pendingTeacherRequestCopy: string,
    pendingClassRequestCopy: string
  ) => (
    <React.Fragment>
      {pendingTeacherRequestCopy && <Text element="p">{pendingTeacherRequestCopy}</Text>}
      {pendingClassRequestCopy && <Text>{pendingClassRequestCopy}</Text>}
    </React.Fragment>
  );

  // Approve pending requests dialog actions

  approveRequests = () => {
    this.handleApprovalSubmit();
    this.hideApprovePendingRequestsDialog();
  };

  hideApprovePendingRequestsDialog = () => {
    this.setState({ approvePendingRequestsDialogIsOpen: false });
  };

  // Backfill dialog actions

  applyBackfill = () => {
    // Placeholder for all the actions needed when the submit button is clicked i.e. mutation + close dialog
    this.hideBackfillDialog();
  };

  hideBackfillDialog = () => {
    this.setState({ backfillDialogIsOpen: false });
  };

  // Handle form changes and submit

  handleTeacherSignupChange = (option: OrgTeacherSignupState) => {
    this.setState({ teacherSignupSelect: option });

    if (option === OrgTeacherSignupState.open) {
      this.setState({ studentAndParentClassCreationSelect: OrgClassCreationState.open });
    }
  };

  handleStudentAndParentClassCreationChange = (option: OrgClassCreationState) => {
    this.setState({ studentAndParentClassCreationSelect: option });
  };

  // This takes the name and orgId because the underlying component is used in other places and requires/returns them.
  //  For this use only the code parameter matters
  handleValidClassCodeChange = (name: string, code: string, orgId?: string) => {};

  toggleSchoolAdminsCanSendUrgentMessage = () => {
    if (this.state.schoolAdminsCanSendUrgentMessageSelect === SchoolAdminsCanSendUrgentMessageState.on) {
      this.setState({ schoolAdminsCanSendUrgentMessageSelect: SchoolAdminsCanSendUrgentMessageState.off });
    } else {
      this.setState({ schoolAdminsCanSendUrgentMessageSelect: SchoolAdminsCanSendUrgentMessageState.on });
    }
  }

  codeIsValid = (code: string) => {
    const groupCodeMaxLength = SettingsStore.getMaxClassCodeLength();
    const groupCodeMinLength = SettingsStore.getMinClassCodeLength();

    let valid =
      code.length >= groupCodeMinLength &&
      code.length <= groupCodeMaxLength &&
      GROUP_CODE_VALIDATION_REGEX.test(code);

    return valid;
  };

  getCurrentCodeValue: () => string | undefined = () => {
    return this.state.classCodeText;
  };

  validateGroupCodeUniqueness = debounce((code: string) => {
    if (!this.isMounted()) {
      return;
    }

    this.setState({ classCodeValid: false });

    // This logic is copied verbatim from GroupForm.tsx
    Group.validateCodeUniqueness(code)
      .then((codes: any) => {
        // XXX move promise out of this view
        if (!this.isMounted()) {
          return;
        }

        // Disable the class code input's loading indicator
        this.setState({ verifyingCodeUniqueness: false });

        // If the code has changed since we dispatched this request,
        // then result is no longer valid, so don't overwrite the error message
        // This prevents these responses from overwriting length validation results.
        if (code !== this.getCurrentCodeValue()) {
          return;
        }

        // The server returns an array of suggested code when the code passed
        // to it was not unique (i.e. a duplicate)
        if (codes) {
          this.setState({
            classCodeError: 'This code is already in use. Please choose another.',
          });
        } else {
          // If no codes are returned, the code passed in was unique
          // so mark it valid
          this.setState({ classCodeText: code, classCodeValid: true });
        }
      })
      .catch(errorLogger('AdminOrgApprovalSettings validateCodeUniqueness failed'));
  }, 300);

  handleCodeChange = (code: string) => {
    // Clear previous errors
    this.setState({
      classCodeError: null,
      verifyingCodeUniqueness: false,
      classCodeText: code,
      classCodeValid: false,
    });

    if (code.length === 0) {
      this.setState({ classCodeError: 'A code is required.' });
      return;
    }

    // If we have gone back to the original code, we are good.
    if (code === this.state.classCodeInitialText) {
      this.setState({ classCodeError: null });
      return;
    }

    // check if the code is valid with client-side checks
    if (code && this.codeIsValid(code)) {
      // Turn on the class code input's loading indicator
      this.setState({ verifyingCodeUniqueness: true });

      // Then call the server to check if the code is unique
      this.validateGroupCodeUniqueness(code);
    }
  };

  handleClassCodeSubmit = async () => {
    const code = this.state.classCodeText;
    const { router, groupCode, location } = this.props;

    // we should never be called in this case, but just in case, error out
    if (!this.userHasChangedClassCode() || !this.state.classCodeValid) {
      console.error('Code submitted without being changed.  Should not be possible.');
      return;
    }

    const attributes = {
      name: code,
    };

    const groupByCode = await loadGQLUserGroupByCode(groupCode);

    if (!groupByCode || !groupByCode.staff_class_org_id) {
      console.error('Got bad group back for request on code: ', code);
      return;
    }

    const groupFromLoadStaffClassForOrgId = await loadStaffClassForOrgId(
      groupByCode.staff_class_org_id
    );

    if (!groupFromLoadStaffClassForOrgId) {
      console.error(
        'Got bad group back for request on loadStaffClassForOrgId: ',
        groupByCode.staff_class_org_id
      );
      return;
    }

    GroupActions.patch(groupFromLoadStaffClassForOrgId, attributes)
      .then(() => {
        this.setState({ classCodeInitialText: code, classCodeText: code });
        let url = location.pathname;
        url = url.replace(`classes/${groupCode}/`, `classes/${code}/`);
        router.replace(url);
        ToastActions.showSuccessToast('Saved');
      })
      .catch((error: any) => ToastActions.showAPIError(error));
  };

  handleApprovalSubmit = () => {
    const { onOrgApprovalSettingsSave, orgUuid } = this.props;
    const { teacherSignupSelect, studentAndParentClassCreationSelect } = this.state;

    onOrgApprovalSettingsSave({
      variables: {
        input: {
          orgUuid,
          gateTeacherRole: teacherSignupSelect === OrgTeacherSignupState.requireApproval,
          gateClasses:
            studentAndParentClassCreationSelect === OrgClassCreationState.requireApproval,
        },
      },
    });

    this.setState({
      teacherSignupInitialSelect: teacherSignupSelect,
      studentAndParentClassCreationInitialSelect: studentAndParentClassCreationSelect,
    });
  };

  handleSave = () => {
    if (this.userHasChangedSchoolAdminsCanSendUrgentMessage()) {
      this.handleSchoolAdminsCanSendUrgentMessageSubmit();
    }

    if (this.userHasChangedApprovalSettings()) {
      if (this.state.teacherSignupSelect === OrgTeacherSignupState.requireApproval) {
        if (this.state.studentAndParentClassCreationSelect === OrgClassCreationState.open) {
          this.setState({ confirmTeacherSignupRequireApprovalDialogIsOpen: true });
        } else {
          this.setState({ confirmAllRequireApprovalDialogIsOpen: true });
        }
      } else {
        this.setState({ approvePendingRequestsDialogIsOpen: true });
      }
    }
  }

  handleSchoolAdminsCanSendUrgentMessageSubmit = () => {
    const { onDistrictSettingsSave, orgId } = this.props;
    const { schoolAdminsCanSendUrgentMessageSelect } = this.state;

    onDistrictSettingsSave({
      variables: {
        input: {
          orgId,
          schoolAdminsCanSendUrgentMessage:
            schoolAdminsCanSendUrgentMessageSelect === SchoolAdminsCanSendUrgentMessageState.on,
        },
      },
    });

    this.setState({
      schoolAdminsCanSendUrgentMessageInitialSelect: schoolAdminsCanSendUrgentMessageSelect,
    });
  };

  // render radio buttons

  renderTeacherSignupRadio = () => {
    const { orgType } = this.props;
    const { teacherSignupSelect } = this.state;
    const radioButtons = (Object.keys(
      OrgTeacherSignupState
    ) as OrgTeacherSignupState[]).map(orgTeacherSignupOption => (
      <Radio
        key={orgTeacherSignupOption}
        label={renderRadioSelectLabel(
          AdminOrgApprovalSettingsConstants.TEACHER_SIGNUP_TO_LABEL[orgTeacherSignupOption],
          orgType === 'District' ? AdminOrgApprovalSettingsConstants.TEACHER_SIGNUP_TO_HELPTEXT_DISTRICT[orgTeacherSignupOption] : AdminOrgApprovalSettingsConstants.TEACHER_SIGNUP_TO_HELPTEXT_SCHOOL[orgTeacherSignupOption],
          false
        )}
        checked={orgTeacherSignupOption === teacherSignupSelect}
        onChange={() => this.handleTeacherSignupChange(orgTeacherSignupOption)}
        disabled={false}
        data-test={`teacher-signup-${orgTeacherSignupOption}`}
      />
    ));
    return renderRadioSelectField(
      AdminOrgApprovalSettingsConstants.HEADING_FOR_SETTING_TYPE.teacherSignup,
      radioButtons as React.ReactElement[]
    );
  };

  renderStudentAndParentClassCreationRadio = () => {
    const { orgType } = this.props;
    const { teacherSignupSelect, studentAndParentClassCreationSelect } = this.state;
    const radioButtons = (Object.keys(OrgClassCreationState) as OrgClassCreationState[]).map(
      orgClassCreationOption => {
        const enabled = !(
          teacherSignupSelect === OrgTeacherSignupState.open &&
          orgClassCreationOption === OrgClassCreationState.requireApproval
        );

        const RadioButtonElement = (
          <Radio
            key={orgClassCreationOption}
            label={renderRadioSelectLabel(
              AdminOrgApprovalSettingsConstants.CLASS_CREATION_TO_LABEL[orgClassCreationOption],
              orgType === 'District' ? AdminOrgApprovalSettingsConstants.CLASS_CREATION_TO_HELPTEXT_DISTRICT[orgClassCreationOption] : AdminOrgApprovalSettingsConstants.CLASS_CREATION_TO_HELPTEXT_SCHOOL[orgClassCreationOption],
              false
            )}
            checked={studentAndParentClassCreationSelect === orgClassCreationOption}
            onChange={() => this.handleStudentAndParentClassCreationChange(orgClassCreationOption)}
            disabled={!enabled}
            data-test={`class-creation-${orgClassCreationOption}`}
          />
        );

        if (!enabled) {
          return (
            <LayerTrigger
              layer={<Tooltip>{AdminOrgApprovalSettingsConstants.CLASS_CREATION_TOOLTIP}</Tooltip>}
              delay={50}
              key={`class-creation-${orgClassCreationOption}-layer-trigger`}
              placement="bottom">
              <div>{RadioButtonElement}</div>
            </LayerTrigger>
          );
        }
        return RadioButtonElement;
      }
    );
    return renderRadioSelectField(
      AdminOrgApprovalSettingsConstants.HEADING_FOR_SETTING_TYPE.classCreation,
      radioButtons as React.ReactElement[]
    );
  };

  renderApprovalsBlock = () => {
    const { APPROVAL_HEADER_TITLE, APPROVAL_HEADER_SUBTEXT } = AdminOrgApprovalSettingsConstants;

    const { orgType } = this.props;
    const { studentAndParentClassCreationSelect, teacherSignupSelect } = this.state;

    const submitIsEnabled =
      this.userHasChangedSchoolAdminsCanSendUrgentMessage() ||
      (this.userHasChangedApprovalSettings() &&
      !(
        teacherSignupSelect === OrgTeacherSignupState.open &&
        studentAndParentClassCreationSelect === OrgClassCreationState.requireApproval
      ));

    return (
      <Container size="medium" align="left" flush>
        <TitleHeader
          title={APPROVAL_HEADER_TITLE}
          subtext={
            <Text>
              {APPROVAL_HEADER_SUBTEXT.text}<br/>
              <Link href={APPROVAL_HEADER_SUBTEXT.learnMoreLink.href} target="_blank">
                {APPROVAL_HEADER_SUBTEXT.learnMoreLink.copy}
              </Link>
              {orgType === 'District' && <><br/><br/>{APPROVAL_HEADER_SUBTEXT.additionalText}</>}
            </Text>
          }
          textAlign="left"
          size="small"
        />
        {this.renderTeacherSignupRadio()}
        {this.renderStudentAndParentClassCreationRadio()}
        <Divider size="medium" />
        <Button
          disabled={!submitIsEnabled}
          onClick={this.handleSave}
          data-test="submit-button">
          Save
        </Button>
      </Container>
    );
  };

  renderDistrictUrgentMessagingSettingsBlock = () => {
    const {
      SCHOOL_URGENT_MESSAGING_TITLE,
      SCHOOL_URGENT_MESSAGING_LABEL,
      SCHOOL_URGENT_MESSAGING_HELPTEXT,
    } = AdminOrgApprovalSettingsConstants;
    const { schoolAdminsCanSendUrgentMessageSelect } = this.state;
    const submitIsEnabled = this.userHasChangedSchoolAdminsCanSendUrgentMessage();
    const canSeeTeacherApprovals = CurrentUser.getUser().hasFeature('org_approval_settings.web.mar-20');

    return (
      <Container size="medium" align="left" flush data-test="district-urgent-messaging-settings">
        <Row size="small">
        <TitleHeader
          title={SCHOOL_URGENT_MESSAGING_TITLE}
          textAlign="left"
          size="small"
          flush
        />
        </Row>
        <Checkbox
          label={renderRadioSelectLabel(SCHOOL_URGENT_MESSAGING_LABEL, SCHOOL_URGENT_MESSAGING_HELPTEXT)}
          checked={schoolAdminsCanSendUrgentMessageSelect === SchoolAdminsCanSendUrgentMessageState.on}
          onChange={() => this.toggleSchoolAdminsCanSendUrgentMessage()}
          data-test={`school-admins-can-send-urgent-message-checkbox`}
        />
        {!canSeeTeacherApprovals && <Button
          disabled={!submitIsEnabled}
          onClick={this.handleSchoolAdminsCanSendUrgentMessageSubmit}
          data-test="district-urgent-messaging-settings-submit-button">
          Save
        </Button>}
      </Container>
    );
  };

  renderDistrictSettings = () => {
    if (this.props.orgType !== 'District') {
      return null;
    }

    return (
      <Container size="medium" align="left" flush>
        {this.props.canSendUrgentMessage && this.renderDistrictUrgentMessagingSettingsBlock()}
      </Container>
    );
  };

  renderClassCodeEditBlock = () => {
    const { CLASSCODE_HEADER_TITLE, CLASSCODE_HEADER_SUBTEXT } = AdminOrgApprovalSettingsConstants;

    const { orgType } = this.props;
    const { classCodeValid } = this.state;
    const groupCodeMaxLength = SettingsStore.getMaxClassCodeLength();
    const groupCodeMinLength = SettingsStore.getMinClassCodeLength();

    const submitIsEnabled = this.userHasChangedClassCode() && classCodeValid;

    return (
      <Container size="large" align="left" flush>
        <TitleHeader
          title={CLASSCODE_HEADER_TITLE[orgType]}
          subtext={
            <React.Fragment>
              <Row size="xsmall">
                <Text>{CLASSCODE_HEADER_SUBTEXT[orgType]}</Text>
              </Row>
              <Row size="xxsmall">
                <MediaObject gutter="medium" figure="right">
                  <FormElement
                    data-test="class-group-code-field"
                    label={null}
                    labelHidden
                    validate={e => {
                      assertions.lengthGreaterThanOrEqualTo(
                        e.target.value,
                        groupCodeMinLength,
                        `The code must be at least ${groupCodeMinLength} characters long.`
                      );
                      assertions.lengthLesserThanOrEqualTo(
                        e.target.value,
                        groupCodeMaxLength,
                        `The code must be ${groupCodeMaxLength} characters or less.`
                      );
                      assertions.matchRegex(
                        e.target.value,
                        GROUP_CODE_VALIDATION_REGEX,
                        'The code can only contain characters a-Z and -'
                      );
                      assertions.notEmpty(e.target.value, 'A code is required');
                    }}
                    onValidChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      this.handleCodeChange(e.target.value)
                    }
                    onInvalidChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      this.setState({ classCodeText: e.target.value, classCodeValid: false });
                    }}
                    showErrorBeforeBlur
                    manualError={this.state.classCodeError}
                    loading={this.state.verifyingCodeUniqueness}
                    element={PrependedTextInput}
                    elementProps={{
                      required: true,
                      name: 'name',
                      value: this.state.classCodeText,
                      className: 'ClassCode-groupCode',
                      prepend: '@',
                    }}
                  />
                  <Button
                    className="ClassCode-submit"
                    disabled={!submitIsEnabled}
                    size="xsmall"
                    onClick={this.handleClassCodeSubmit}
                    data-test="classcode-submit-button">
                    Save
                  </Button>
                </MediaObject>
              </Row>
            </React.Fragment>
          }
          textAlign="left"
          size="small"
          flush
        />
      </Container>
    );
  };

  render() {
    const {
      approvePendingRequestsDialogIsOpen,
      backfillDialogIsOpen,
      unsavedChangesDialogIsOpen,
      confirmTeacherSignupRequireApprovalDialogIsOpen,
      confirmAllRequireApprovalDialogIsOpen,
    } = this.state;
    const {
      PENDING_REQUESTS_MODAL,
      BACKFILL_MODAL,
      HEADER_TITLE,
      CONFIRM_DIALOG,
      CONFIRM_TEACHER_SIGNUP_REQUIRE_APPROVAL_MODAL,
      CONFIRM_ALL_REQUIRE_APPROVAL_MODAL
    } = AdminOrgApprovalSettingsConstants;

    const currentUser = CurrentUser.getUser();
    const canSeeTeacherApprovals = currentUser?.hasFeature('org_approval_settings.web.mar-20');
    const canSeeClassCodeEdits = currentUser?.hasFeature('org_classcode_settings.web.apr-20');

    return (
      <div>
        <Modal isOpen={unsavedChangesDialogIsOpen} data-test="unsaved-changes-dialog">
          <ConfirmDialog
            glyph="caution"
            glyphColor="apple"
            title={CONFIRM_DIALOG.title}
            subtext={CONFIRM_DIALOG.body}
            onConfirm={this.forwardUserToNextLocation}
            onCancel={this.hideUnsavedChangesDialog}
            actionButtonText={CONFIRM_DIALOG.buttons.action}
            cancelButtonText={CONFIRM_DIALOG.buttons.cancel}
            onRequestHide={this.hideUnsavedChangesDialog}
          />
        </Modal>
        <Modal
          isOpen={approvePendingRequestsDialogIsOpen}
          data-test="approve-pending-requests-dialog">
          <ConfirmDialog
            title={PENDING_REQUESTS_MODAL.title}
            subtext={PENDING_REQUESTS_MODAL.subtext}
            actionButtonText={PENDING_REQUESTS_MODAL.buttons.submit.copy}
            onConfirm={this.approveRequests}
            cancelButtonText={PENDING_REQUESTS_MODAL.buttons.cancel.copy}
            onRequestHide={this.hideApprovePendingRequestsDialog}
          />
        </Modal>
        <Modal isOpen={backfillDialogIsOpen} data-test="backfill-dialog">
          <ConfirmDialog
            title={BACKFILL_MODAL.title(111, 222)}
            subtext={this.renderBackfillModalParagraph(
              BACKFILL_MODAL.subtext.teachers(111),
              BACKFILL_MODAL.subtext.classes(222)
            )}
            actionButtonText={BACKFILL_MODAL.buttons.submit.copy}
            onConfirm={this.applyBackfill}
            onRequestHide={this.hideBackfillDialog}
            cancelButtonText={BACKFILL_MODAL.buttons.learnMore.copy}
            onCancel={() => {
              window.open(BACKFILL_MODAL.buttons.learnMore.href);
            }}
          />
        </Modal>
        <Modal
          isOpen={confirmTeacherSignupRequireApprovalDialogIsOpen}
          data-test="confirm-teacher-signup-require-approval-dialog">
          <ConfirmDialog
            size="small"
            title={CONFIRM_TEACHER_SIGNUP_REQUIRE_APPROVAL_MODAL.title}
            subtext={<Text align="left">{CONFIRM_TEACHER_SIGNUP_REQUIRE_APPROVAL_MODAL.subtext}</Text>}
            actionButtonText={CONFIRM_TEACHER_SIGNUP_REQUIRE_APPROVAL_MODAL.buttons.submit.copy}
            onConfirm={this.handleApprovalSubmit}
            cancelButtonText={CONFIRM_TEACHER_SIGNUP_REQUIRE_APPROVAL_MODAL.buttons.cancel.copy}
            onRequestHide={() => this.setState({ confirmTeacherSignupRequireApprovalDialogIsOpen: false })}
          />
        </Modal>
        <Modal
          isOpen={confirmAllRequireApprovalDialogIsOpen}
          data-test="confirm-all-require-approval-dialog">
          <ConfirmDialog
            size="small"
            title={CONFIRM_ALL_REQUIRE_APPROVAL_MODAL.title}
            subtext={<Text align="left">{CONFIRM_ALL_REQUIRE_APPROVAL_MODAL.subtext}</Text>}
            actionButtonText={CONFIRM_ALL_REQUIRE_APPROVAL_MODAL.buttons.submit.copy}
            onConfirm={this.handleApprovalSubmit}
            cancelButtonText={CONFIRM_ALL_REQUIRE_APPROVAL_MODAL.buttons.cancel.copy}
            onRequestHide={() => this.setState({ confirmAllRequireApprovalDialogIsOpen: false })}
          />
        </Modal>
        <Container size="medium" align="left" flush>
          <TitleHeader title={HEADER_TITLE} textAlign="left" />
          {canSeeClassCodeEdits && this.renderClassCodeEditBlock()}
          {this.renderDistrictSettings()}
          {canSeeTeacherApprovals && (
            <React.Fragment>
              <Divider size="medium" />
              {this.renderApprovalsBlock()}
            </React.Fragment>
          )}
        </Container>
      </div>
    );
  }
}
